import { useRef, useEffect } from "react";
import {
  MagnifyingGlass,
  Funnel,
  X,
} from "@phosphor-icons/react";

interface OffersHeaderProps {
  user: any;
  currentLang: "ro" | "en";
  searchQuery: string;
  onSearchChange: (query: string) => void;
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  sortBy: "discount" | "distance" | "popularity";
  onSortChange: (sort: "discount" | "distance" | "popularity") => void;
  showFilters: boolean;
  onFiltersToggle: () => void;
  categories?: any[];
}

export function OffersHeader({
  user: _user,
  currentLang,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  sortBy,
  onSortChange,
  showFilters,
  onFiltersToggle,
  categories,
}: OffersHeaderProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input dacă vine din pagina principală
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const focusSearch = urlParams.get("focusSearch") === "true";
    
    if (focusSearch) {
      // Delay pentru a permite render-ul complet
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          // Scroll la input dacă e necesar
          searchInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          // Elimină query param după focus
          window.history.replaceState({}, "", "/app/offers");
        }
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <img 
            src="/suuper_pin.svg" 
            alt="Suuper" 
            className="h-8 w-auto"
          />
          <p className="text-primary  text-2xl font-semibold">
            {currentLang === "ro" ? "Oferte" : "Offers"}
          </p>
        </div>
       {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto">
        <button
          onClick={onFiltersToggle}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
            showFilters || selectedCategory || sortBy !== "discount"
              ? ""
              : "bg-white/20 text-primary"
          }`}
        >
          <Funnel size={16} />
          {currentLang === "ro" ? "Filtre" : "Filters"}
        </button>

        {selectedCategory && (
          <button
            onClick={() => onCategoryChange(null)}
            className="flex items-center gap-1 px-3 py-1.5 bg-white/20 text-primary rounded-lg text-sm whitespace-nowrap"
          >
            {categories?.find((c) => c.slug === selectedCategory)?.name}
            <X size={3} />
          </button>
        )}
      </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary z-10" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder={currentLang === "ro" ? "Caută oferte..." : "Search offers..."}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-10 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-full text-primary placeholder:text-primary/70 focus:outline-none focus:ring-2 focus:ring-white/50"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange("")}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-white/20 rounded z-10"
          >
            <X size={16} className="text-white" />
          </button>
        )}
      </div>

      

      {/* Expanded Filters */}
      {showFilters && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              {currentLang === "ro" ? "Categorie" : "Category"}
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onCategoryChange(null)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  !selectedCategory
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary"
                }`}
              >
                {currentLang === "ro" ? "Toate" : "All"}
              </button>
              {categories?.map((category) => (
                <button
                  key={category._id}
                  onClick={() => onCategoryChange(category.slug)}
                  className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${
                    selectedCategory === category.slug
                      ? "bg-primary text-white"
                      : "bg-primary/20 text-primary"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              {currentLang === "ro" ? "Sortează după" : "Sort by"}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => onSortChange("discount")}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  sortBy === "discount"
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary"
                }`}
              >
                {currentLang === "ro" ? "Reducere" : "Discount"}
              </button>
              <button
                onClick={() => onSortChange("popularity")}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  sortBy === "popularity"
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary"
                }`}
              >
                {currentLang === "ro" ? "Popularitate" : "Popularity"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
